// src/stores/favoritesStore.ts
/**
 * Favoris LOCAUX — pendant de cartStore pour la navigation sans compte.
 *
 * La plateforme est en accès libre : on peut parcourir le catalogue et mettre
 * de côté des produits sans jamais recevoir de SMS. Ces favoris vivent dans le
 * navigateur, exactement comme le panier.
 *
 * À la première connexion (déclenchée au moment de commander), ils sont
 * remontés dans `Customers/{uid}/Favorites` puis vidés d'ici — voir
 * `mergeLocalFavorites` dans Context/AuthContext.tsx. À partir de là,
 * Firestore redevient la source de vérité (synchro multi-appareils).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface LocalFavorite {
  productId:    string;
  productName:  string;
  productImage: string | null;
  price:        number;
  /** Epoch ms — Date ne survit pas à la sérialisation localStorage. */
  addedAt:      number;
}

export type LocalFavoriteInput = Omit<LocalFavorite, 'addedAt'>;

interface FavoritesState {
  items: LocalFavorite[];
  add:    (fav: LocalFavoriteInput) => void;
  remove: (productId: string) => void;
  /** Bascule le favori et renvoie son nouvel état (true = ajouté). */
  toggle: (fav: LocalFavoriteInput) => boolean;
  has:    (productId: string) => boolean;
  clear:  () => void;
}

export const useFavoritesStore = create<FavoritesState>()(
  persist(
    (set, get) => ({
      items: [],

      add: (fav) => {
        if (get().items.some((i) => i.productId === fav.productId)) return;
        set((s) => ({ items: [{ ...fav, addedAt: Date.now() }, ...s.items] }));
      },

      remove: (productId) =>
        set((s) => ({ items: s.items.filter((i) => i.productId !== productId) })),

      toggle: (fav) => {
        const exists = get().items.some((i) => i.productId === fav.productId);
        if (exists) {
          get().remove(fav.productId);
          return false;
        }
        get().add(fav);
        return true;
      },

      has: (productId) => get().items.some((i) => i.productId === productId),

      clear: () => set({ items: [] }),
    }),
    { name: 'ornoir-favorites' }
  )
);

export const useLocalFavoriteCount = () => useFavoritesStore((s) => s.items.length);

export const useIsLocalFavorite = (productId: string | null | undefined) =>
  useFavoritesStore((s) => (productId ? s.items.some((i) => i.productId === productId) : false));
